import React from 'react';
import _ from 'lodash-es';
import { Table, Link, CardHeader } from '../library-components';
import { Inspector } from '@quintype/em/components/inspector';
import { PublishedAt } from './published_at';
import Select, { AsyncSelect } from '@quintype/em/components/select';
import { TextField } from '@quintype/em/components/text-field';
import { NumberField } from '@quintype/em/components/number-field';
import './assets.scss';
import validate from 'validate.js';
import { getSearchCustomAttributes } from '../../api';

const INHERITANCE_CATEGORY_NAME = 'assets';

function immutableSplice (arr, start, deleteCount, ...items) {
  return [...arr.slice(0, start), ...items, ...arr.slice(start + deleteCount)];
}

const getOptions = items => {
  if (items) {
    return items.map(k => ({ label: k.name, value: k.id }));
  }
};

const maximumPublishedAtDuration = {
  days: 36524,
  weeks: 5217,
  months: 1200,
  years: 100
};

const constraints = (durationUnit) => {
  return {
    duration_length: { presence: true, numericality: { strict: true, onlyInteger: true, greaterThan: 0, lessThanOrEqualTo: maximumPublishedAtDuration[durationUnit] } }
  };
};

const AssetMetaFields = ({ asset, metaFields, updateAssetMetadata, updateAsset, inheritanceCategoryId, assetFrom }) => {
  var assetCategory = _.find(metaFields, ['id', asset.asset_category_id]);
  const metadataFieldName = _.keys(_.get(asset, ['metadata']))[0];
  const value = asset['metadata'][metadataFieldName];
  const options = _.get(assetCategory['metadata'], [metadataFieldName, 'values'], []);
  const valueForSelect = _.find(options, { value: value });

  const promiseOptions = inputValue => {
    if (assetCategory['metadata'][metadataFieldName]['search_url']) {
      const url = `${assetCategory['metadata'][metadataFieldName]['search_url']}?q=${inputValue}`;

      return getSearchCustomAttributes(url)
        .then(res => res);
    } else {
      return new Promise(resolve => resolve([]));
    }
  };

  if (assetCategory) {
    if (!_.isEmpty(assetCategory.metadata)) {
      if (metadataFieldName === 'access_level') {
        return (
          <NumberField
            value={value}
            label='Value'
            placeholder='Access Level Value'
            onChange={e => updateAssetMetadata(metadataFieldName, e)} />
        );
      }
      return (
        <AsyncSelect
          value={valueForSelect}
          label='Value'
          defaultOptions={options}
          loadOptions={promiseOptions}
          onChange={e => updateAssetMetadata(metadataFieldName, e.value)}
        />
      );
    } else if (assetCategory.id === inheritanceCategoryId) {
      return (
        <Select
          label='Assets From'
          value={asset.asset_parentable_id || ''}
          options={getOptions(assetFrom)}
          onChange={(e) => updateAsset('asset_parentable_id', e ? e.value : null)}
        />
      );
    } else {
      return null;
    }
  } else {
    return null;
  }
};

export default class Assets extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      asset: { asset_category_id: null, id: null, title: null, metadata: {}, asset_parentable_id: null, published_at: {} },
      isCreateAssetOpen: false,
      isSaveAssetsEnabled: false,
      errors: {}
    };
    this.updateAssetMetadata = this.updateAssetMetadata.bind(this);
    this.updateAsset = this.updateAsset.bind(this);
  }

  componentWillReceiveProps (nextProps) {
    if (nextProps.errors.length === 0) {
      this.setState({ isCreateAssetOpen: false, asset: { asset_category_id: null, id: null, title: null, metadata: {}, asset_parentable_id: null } });
    }
    return true;
  }

  updateAssetMetadata (field, value) {
    const metadata = this.state.asset.metadata;
    let isSaveAssetsEnabled = false;
    if (value.trim() !== '') {
      metadata[field] = value;
      isSaveAssetsEnabled = true;
    } else {
      delete metadata[field];
    }
    this.setState({ asset: Object.assign({}, this.state.asset, { metadata }), isSaveAssetsEnabled: isSaveAssetsEnabled });
  }

  updateAsset (field, value) {
    if (field === 'asset_category_id') {
      const newAsset = Object.assign({}, this.state.asset);
      delete newAsset.asset_parentable_id;
      const isSaveAssetsEnabled = value && !_.isEmpty(value) && !_.startsWith(value, 'story ') && value !== this.getInheritanceCategoryId();
      let assetCategoryId;
      if (_.startsWith(value, 'story ')) {
        assetCategoryId = _.find(this.props.assetCategories, { name: 'story' })['id'];
        const metadataKey = _.replace(value, 'story ', '');
        this.setState({ asset: Object.assign({}, newAsset, { [field]: assetCategoryId, metadata: { [metadataKey]: '' } }), isSaveAssetsEnabled: isSaveAssetsEnabled });
      } else {
        assetCategoryId = _.find(this.props.assetCategories, { name: value })['id'];
        this.setState({ asset: Object.assign({}, newAsset, { [field]: assetCategoryId, metadata: {} }), isSaveAssetsEnabled: isSaveAssetsEnabled });
      }
      return;
    } else if (field === 'asset_parentable_id') {
      const isSaveAssetsEnabled = !!value;
      this.setState({ asset: Object.assign({}, this.state.asset, { [field]: value, metadata: {} }), isSaveAssetsEnabled: isSaveAssetsEnabled });
      return;
    }
    this.setState({ asset: Object.assign({}, this.state.asset, { [field]: value }), isSaveAssetsEnabled: this.enableSaveAssets(field, value) });
  }

  enableSaveAssets (field, value) {
    if (this.state.asset.asset_category_id === this.getInheritanceCategoryId() && !this.state.asset.asset_parentable_id) {
      return false;
    } else if (_.startsWith(_.toString(this.state.asset.asset_category_id), '999') && _.isEmpty(this.state.asset.metadata)) {
      return false;
    } else if (!this.state.asset.asset_category_id) {
      return false;
    } else if (field === 'published_at') {
      if (value.to && value.to.duration_length && value.to.duration_unit) {
        const errors = validate(value.to, constraints(value.to.duration_unit));
        if (errors) {
          this.setState({ errors: errors });
          return false;
        } else {
          this.setState({ errors: {} });
        }
      }
      if (Object.keys(value).every(key => value[key] !== null)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  addNewAsset (e) {
    this.setState({ isCreateAssetOpen: true, isSaveAssetsEnabled: false, asset: { asset_category_id: null, id: null, title: null, metadata: {}, asset_parentable_id: null } });
  }

  onSaveAsset (e) {
    const assets = [...this.props.assets];

    if (_.includes(_.keys(this.state.asset), 'index')) {
      const index = this.state.asset.index;
      assets.splice(index, 1);
      assets.push(this.state.asset);
      this.props.onChange(assets); // for edit
    } else {
      assets.push(this.state.asset);
      this.props.onChange(assets); // for new asset
    }
  }

  getAssetCategoryMetafields () {
    return this.props.assetCategories.map((k) => ({ id: k['id'], metadata: k['metadata_fields'] }));
  }

  getCategoryId (asset) {
    const category = this.props.assetCategories.find(category => category.id === asset.asset_category_id);
    if (!category) {
      const categoryName = Object.keys(asset.metadata)[0];
      const category = this.props.assetCategories.find(category => category.name === 'story ' + categoryName);
      return category.id;
    } else {
      return asset.asset_category_id;
    }
  }

  getAssetMetadata (asset) {
    const category = this.props.assetCategories.find(category => category.id === asset.asset_category_id);
    if (!category && !asset.asset_parentable_id) {
      const metadataValue = asset.metadata && Object.values(asset.metadata)[0];
      return metadataValue ? { id: metadataValue } : {};
    } else {
      return asset.metadata || {};
    }
  }

  deleteAsset (e, index) {
    e.preventDefault();
    this.props.onChange(immutableSplice(this.props.assets, index, 1));
  }

  onEdit (asset, index) {
    this.setState({
      isCreateAssetOpen: true,
      isSaveAssetsEnabled: false,
      asset: {
        id: asset.id,
        asset_category_id: asset.asset_category_id ? this.getCategoryId(asset) : this.getInheritanceCategoryId(),
        metadata: this.getAssetMetadata(asset),
        published_at: asset.published_at,
        title: asset.title,
        asset_parentable_id: asset.asset_parentable_id,
        asset_parentable_type: asset.asset_parentable_type,
        index: index
      }
    });
  }

  getInheritanceCategoryId () {
    const inheritanceCategory = _.find(this.props.assetCategories, (item) => item.name === INHERITANCE_CATEGORY_NAME);
    return inheritanceCategory ? inheritanceCategory.id : null;
  }

  getAssetTypeOptions (assetCategories) {
    if (assetCategories) {
      const temp = assetCategories.map(k => {
        if (!_.isEmpty(k.metadata_fields)) {
          return Object.keys(k.metadata_fields).map((key) => ({ label: `story ${key}`, value: `story ${key}` }));
        } else {
          return { label: k.name, value: k.name };
        }
      });

      return _.flatten(temp);
    }
  }

  getAssetTypeValue () {
    if (!this.state.asset.asset_category_id) {
      return '';
    }
    const assetCategory = _.find(this.props.assetCategories, (item) => item.id === this.state.asset.asset_category_id);

    if (_.isEmpty(this.state.asset.metadata)) {
      return assetCategory.name;
    } else {
      return `${assetCategory.name} ${_.keys(this.state.asset.metadata)[0]}`;
    }
  }

  render () {
    const options = this.getAssetTypeOptions(this.props.assetCategories);
    return (
      <div>
        { this.props.assets.length === 0 && <CardHeader
          title='Assets'
          dataTest='add_assets'
          buttonText='Add Assets'
          buttonOnClick={() => this.addNewAsset()}
          isButton={this.props.id !== 'new'}
        /> }
        {this.state.isCreateAssetOpen && (
          <Inspector
            title={ this.state.asset.id || this.state.asset.asset_parentable_id ? 'Edit asset' : 'Add asset'}
            isActive={this.state.isCreateAssetOpen}
            onClose={() => this.setState({ isCreateAssetOpen: false })}
            actionButtonLabel='Save'
            onActionButtonClick={e => this.onSaveAsset(e)}
            isActionButtonDisabled={!this.state.isSaveAssetsEnabled}
          >
            <TextField
              value={this.state.asset.title || ''}
              label='Asset Name'
              placeholder='Asset Name'
              onChange={e => this.updateAsset('title', e)}
            />
            <Select
              value={this.getAssetTypeValue()}
              label='Type'
              options={options}
              onChange={e => this.updateAsset('asset_category_id', e.value)}
            />
            {this.props.assetCategories && this.state.asset.asset_category_id && (
              <AssetMetaFields asset={this.state.asset}
                metaFields={this.getAssetCategoryMetafields()}
                updateAssetMetadata={this.updateAssetMetadata}
                updateAsset={this.updateAsset}
                inheritanceCategoryId={this.getInheritanceCategoryId()}
                assetFrom={this.props.assetFrom}
              />
            )}
            { this.props.showPublishedAt &&
              <PublishedAt updateAsset = {(field, value) => this.updateAsset(field, value)}
                published_at_field = {this.state.asset.published_at}
                errors = {this.state.errors}
              />
            }
          </Inspector>
        )}
        <Table header={['Type', 'Name', 'Conditions', 'Published At', '']}>
          {
            _.map(this.props.assets, (asset, index) => (
              <div className='table-default__row' key={index}>
                <div className='table-default__cell'>
                  {_.get(asset, 'type') || 'assets'}
                </div>
                <div className='table-default__cell'>{_.get(asset, 'title')}</div>
                <div className='table-default__cell'>
                  {JSON.stringify(_.get(asset, ['metadata'])) ||
                  _.get(asset, 'asset_parentable_name') ||
                  '_'}
                </div>
                <div className='table-default__cell'>
                  {_.get(asset, ['published_at', 'from']) !== undefined ? 'Custom Duration' : 'Anytime'}
                </div>
                <div className='table-default__cell'>
                  <Link onClick={() => this.onEdit(asset, index)}>Edit</Link>
                  <Link onClick={e => this.deleteAsset(e, index)} destructive>
                  Delete
                  </Link>
                </div>
              </div>
            ))}
        </Table>
      </div>
    );
  }
}
